import { createFileRoute, Link } from "@tanstack/react-router";
import { format, parseISO, subDays, eachDayOfInterval } from "date-fns";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AppHeader } from "@/components/app/AppHeader";
import { KpiRow } from "@/components/app/KpiRow";
import { useAllProfiles, useAllSubmissions } from "@/hooks/use-admin-data";
import { useAuth, DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import { DEPARTMENT_CONFIGS } from "@/lib/department-configs";
import { modCanWorkspace, modCanDept, type ModeratorPermissions } from "@/lib/moderator-permissions";

export const Route = createFileRoute("/_app/_team/team/overview")({
  component: TeamOverview,
});

function TeamOverview() {
  const { profile, role } = useAuth();
  const perms = (profile?.moderator_permissions ?? {}) as ModeratorPermissions;
  const isAdmin = role === "admin";
  const canOverview = isAdmin || modCanWorkspace(perms, "view_overview");

  const { data: profiles = [] } = useAllProfiles();
  const { data: submissions = [] } = useAllSubmissions();

  if (!canOverview) {
    return (
      <>
        <AppHeader subtitle="Team overview" />
        <div className="px-[22px] pb-6 text-[12px] text-muted-foreground">
          You don't have permission to view the workspace overview.
        </div>
      </>
    );
  }

  // Departments visible to this user (admin = all; mod = only those with view)
  const allDepts = Object.keys(DEPARTMENT_CONFIGS) as Department[];
  const visibleDepts = isAdmin
    ? allDepts
    : allDepts.filter((d) => modCanDept(perms, d, "view"));

  const now = new Date();
  const monthSubs = submissions.filter((s) => {
    const d = parseISO(s.submission_date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const activeUsers = profiles.filter((p) => p.status === "active").length;
  const approved = monthSubs.filter((s) => s.status === "approved").length;
  const corrections = monthSubs.filter((s) => s.status === "correction_needed").length;
  const reviewed = monthSubs.filter((s) => s.status !== "pending").length;
  const approvalRate = reviewed ? Math.round((approved / reviewed) * 100) : 0;

  const start = subDays(now, 29);
  const days = eachDayOfInterval({ start, end: now });
  const dayMap = new Map<string, number>();
  for (const s of submissions) {
    dayMap.set(s.submission_date, (dayMap.get(s.submission_date) ?? 0) + 1);
  }
  const trend = days.map((d) => ({
    date: format(d, "dd MMM"),
    count: dayMap.get(format(d, "yyyy-MM-dd")) ?? 0,
  }));

  const deptAgg = visibleDepts.map((d) => {
    const subs = monthSubs.filter((s) => s.department === d);
    return {
      dept: d,
      label: DEPARTMENT_LABELS[d],
      count: subs.length,
      points: subs.reduce((s, r) => s + r.points, 0),
      people: profiles.filter((p) => p.department === d).length,
    };
  });

  return (
    <>
      <AppHeader subtitle="Workspace-wide performance" />
      <KpiRow
        items={[
          { label: "Visible Users", value: String(activeUsers), variant: "primary" },
          { label: "Submissions This Month", value: String(monthSubs.length), trend: `${approved} approved` },
          { label: "Open Corrections", value: String(corrections), variant: "danger", trend: "needs review" },
          { label: "Approval Rate", value: reviewed ? `${approvalRate}%` : "—", trend: `${reviewed} reviewed` },
          { label: "Departments", value: String(visibleDepts.length), trend: "you can see" },
        ]}
      />

      <div className="grid gap-4 px-[22px] pb-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
            Output
          </div>
          <h2 className="text-[18px] font-bold">30-Day Trend</h2>
          <div className="mt-4 h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="team-out" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} tickLine={false} axisLine={false} interval={4} />
                <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid var(--border)", background: "var(--card)", color: "var(--foreground)" }} />
                <Area type="monotone" dataKey="count" stroke="var(--primary)" strokeWidth={2} fill="url(#team-out)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
            By Department
          </div>
          <h2 className="text-[18px] font-bold">This Month</h2>
          <div className="mt-3 flex flex-col gap-2">
            {deptAgg.length === 0 && (
              <p className="text-[12px] text-muted-foreground">No departments visible to you.</p>
            )}
            {deptAgg.map((d) => (
              <Link
                key={d.dept}
                to="/team/departments"
                search={{ dept: d.dept } as never}
                className="flex items-center justify-between rounded-md border border-border px-3 py-2 transition-colors hover:bg-muted/50"
              >
                <div className="min-w-0">
                  <div className="text-[12px] font-semibold">{d.label}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {d.people} {d.people === 1 ? "person" : "people"}
                    {d.dept === "editor" ? ` · ${d.points} pts` : ""}
                  </div>
                </div>
                <span className="text-[14px] font-bold">{d.count}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
